import {
    ICheckAddress, IGetAddressList, IServiceCompleteOrder, IServiceGetCouponCodeById,
    IServiceGetCouponByCode, IServiceGetCouponById,
    IServiceGetGiftCardBalance, IServiceGetLastWaitTimes,
    IServiceProposedOrder, IServiceSaveOrder, IServiceValidatePromotionCode, IServiceValidatePromotionCodeResult, IServiceGetCouponByCodeOnly,
    IReserveLimitedTimes,
    IGetLimitedTimes,
    IResetLimitedTimeTimer,
    IRemoveOrderLimitedTimes,
    IThirdPartyAddressModel
} from "./online-ordering-service.interface";
import { Util } from "../utils/util";
import { HttpService } from "./http-service";
import { IService } from "./service.interface";
import { Data } from "../models/data";

export class OnlineOrderingService implements IService {
    private readonly _http: HttpService;
    private readonly _data: Data;
    private readonly _apiOnlineOrderingUrl: string;
    private readonly _apiOrderUrl: string;
    private readonly _addressLookupUrl: string;
    private readonly _publicKey: string;

    constructor(http: HttpService, data: Data, addressLookupUrl: string, apiOrderUrl: string, apiOnlineOrderingUrl: string, publicKey: string) {
        this._http = http;
        this._data = data;
        this._addressLookupUrl = addressLookupUrl;
        this._apiOrderUrl = apiOrderUrl;
        this._apiOnlineOrderingUrl = apiOnlineOrderingUrl;
        this._publicKey = publicKey;
    }

     public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

         return this._http.request(method, url, payload, storeKey, businessDate, {showProgress: options?.showProgress, noCache: options?.noCache});
    }

    public postJSError = async (data: string): Promise<void> => {
        await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}js-error`, { data: data }, { showProgress: false });
    }

    public thirdPartyEstimate = async (data: IThirdPartyAddressModel): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}third-party-estimate`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public validatePromotionCode = async (data: IServiceValidatePromotionCode): Promise<IServiceValidatePromotionCodeResult> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}validate-promotion-code`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public invalidatePromotionCode = async (data: IServiceValidatePromotionCode): Promise<IServiceValidatePromotionCodeResult> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}invalidate-promotion-code`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public proposedOrder = async (data: any): Promise<IServiceProposedOrder> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}proposed-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public completeOrder = async (data: any): Promise<IServiceCompleteOrder> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}complete-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public saveOrder = async (order: string): Promise<IServiceSaveOrder> => {
        const payload = await Util.encrypt(order, this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}save-order`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public getLastWaitTimes = async (storeKey: string): Promise<IServiceGetLastWaitTimes> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-last-wait-times`, { SKY: storeKey });
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: "{}" };
    }

    public getGiftCardBalance = async (data: { SKY: string, CNO: string, PIN: string }): Promise<IServiceGetGiftCardBalance> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-gift-card-balance`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, balance: 0 };
    }

    public getCouponById = async (data: { CID: number, SKY: string }): Promise<IServiceGetCouponById> => {
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-id`, data);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public getCouponByCouponCode = async (data: IServiceGetCouponByCodeOnly): Promise<IServiceGetCouponCodeById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, couponId: null, serverError: true };
    }

    public getCouponByPromotionCode = async (data: IServiceGetCouponByCode): Promise<IServiceGetCouponById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-promotion-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public getCouponByCampaignCode = async (data: IServiceGetCouponByCode): Promise<IServiceGetCouponById> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOrderUrl}get-coupon-by-campaign-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, coupon: "", serverError: true };
    }

    public getAddressList = async (payload: IGetAddressList): Promise<{ Addr: any[] }> => {
        const response = await this.httpRequestAsync('POST', `${this._addressLookupUrl}/GetStreets`, payload, { showProgress: false, noCache: false });
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { Addr: [] };
    }

    public checkAddress = async (data: ICheckAddress): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}check-address`, payload);
        if (response.status !== 200)
            throw new Error(`check-address failed: HTTP ${response.status}`);

        const json = await this._http.getJson(response);
        if (typeof json !== "boolean")
            throw new Error("check-address failed: unexpected response");

        return json;
    }

    public getLimitedTimes = async (data: IGetLimitedTimes): Promise<{ success: boolean, data: { min: number, cnt: number }[] }> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}get-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: [] };
    }

    public reserveLimitedTimes = async (data: IReserveLimitedTimes): Promise<{ success: boolean, data: { min: number, cnt: number }[] | null }> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}reserve-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, data: null };
    }

    public resetLimitedTimeTimer = async (data: IResetLimitedTimeTimer): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}reset-limited-times-timer`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public removeOrderLimitedTimes = async (data: IRemoveOrderLimitedTimes): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiOnlineOrderingUrl}remove-order-limited-times`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public async updateOrderhubOrder(orderHubKey: string, orderId: number, storeKey: string): Promise<boolean> {
        const response = await this.httpRequestAsync('PATCH', `${this._apiOrderUrl}orderhub-order/?orderHubKey=${orderHubKey}&orderId=${orderId}&storeKey=${storeKey}`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }
}